/**
 * Rapid-fire section intro (spec 29).
 *
 * The clock starts on this button, never on page load — a respondent who arrives mid-scroll or
 * reloads must not lose seconds they never had. The three facts are deliberately reassuring
 * ("0 wrong answers"): the format is meant to feel like a game, not a test.
 */
import React from 'react';
import { Zap } from 'lucide-react';
import Button from '../../components/Button';

interface RapidFireIntroProps {
  label: string;
  introText: string;
  durationSeconds: number;
  onStart: () => void;
  /** True while the Start request is in flight, so the button can't be double-fired. */
  starting?: boolean;
}

const RapidFireIntro: React.FC<RapidFireIntroProps> = ({
  label, introText, durationSeconds, onStart, starting = false,
}) => (
  <div className="text-center px-8 py-12">
    <div className="w-[72px] h-[72px] rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-6">
      <Zap className="w-8 h-8 text-blue-600" aria-hidden="true" />
    </div>

    <h1 className="text-3xl font-bold text-gray-900 mb-3">{label}</h1>
    <p className="text-lg leading-relaxed text-gray-700 max-w-[44ch] mx-auto mb-7">{introText}</p>

    <div className="flex gap-3 justify-center flex-wrap mb-8">
      {[
        { value: `${durationSeconds}s`, caption: 'on the clock' },
        { value: '1', caption: 'question at a time' },
        { value: '0', caption: 'wrong answers' },
      ].map((fact) => (
        <div
          key={fact.caption}
          className="bg-gray-50 border border-gray-200 rounded-lg px-[18px] py-3 min-w-[120px]"
        >
          <span className="block text-xl text-gray-900 tabular-nums font-semibold">
            {fact.value}
          </span>
          <span className="text-xs text-gray-500">{fact.caption}</span>
        </div>
      ))}
    </div>

    <Button variant="primary" onClick={onStart} disabled={starting}>
      {starting ? 'Starting…' : 'Start'}
    </Button>
  </div>
);

export default RapidFireIntro;
